import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import appStoreBadge from '../assets/images/app-store-badge.svg';
import googlePlayBadge from '../assets/images/google-play-badge.svg';
import { useStoreLinks } from '../lib/useStoreLinks';

// Mirrors the key read by public/index.html and src/lib/analytics.ts. Setting it
// suppresses the Meta Pixel + PostHog on the visitor's next load (CCPA/CPRA
// "Do Not Sell or Share" opt-out).
const TRACKING_OPTOUT_KEY = 'dink_tracking_optout';

interface FooterProps {
  bridgeTopSeam?: boolean;
}

const Footer: React.FC<FooterProps> = ({ bridgeTopSeam = false }) => {
  const { appStoreUrl, playStoreUrl, onAppStoreClick, onPlayStoreClick } = useStoreLinks();
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    try {
      setOptedOut(window.localStorage.getItem(TRACKING_OPTOUT_KEY) === '1');
    } catch (e) {
      /* localStorage unavailable */
    }
  }, []);

  const handleOptOut = () => {
    try {
      window.localStorage.setItem(TRACKING_OPTOUT_KEY, '1');
    } catch (e) {
      /* ignore */
    }
    window.location.reload();
  };

  const handleUndoOptOut = () => {
    try {
      window.localStorage.removeItem(TRACKING_OPTOUT_KEY);
    } catch (e) {
      /* ignore */
    }
    window.location.reload();
  };
  return (
    <footer
      className={`bg-primary px-6 md:px-10 py-12 ${bridgeTopSeam ? '-mt-px' : ''}`}
    >
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <Link to="/" className="flex items-start gap-0.5" aria-label="DINK Social home">
          <img src="/DINKSocial_logo_white.svg" alt="DINK Social" className="h-6 w-auto" />
          <span
            className="text-white text-[10px] font-medium leading-none pt-0.5"
            aria-hidden="true"
          >
            ™
          </span>
        </Link>

        <div className="flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] font-medium text-white/70">
          <Link to="/about" className="hover:text-white transition-colors">
            Our Story
          </Link>
          <Link to="/blog" className="hover:text-white transition-colors">
            Dink Journal
          </Link>
          <Link to="/contact" className="hover:text-white transition-colors">
            Contact
          </Link>
          <Link to="/partners" className="hover:text-white transition-colors">
            Partners
          </Link>
          <Link to="/privacy" className="hover:text-white transition-colors">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-white transition-colors">
            Terms
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onAppStoreClick}
            aria-label="Download on the App Store"
          >
            <img src={appStoreBadge} alt="Download on the App Store" className="h-9" />
          </a>
          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onPlayStoreClick}
            aria-label="Get it on Google Play"
          >
            <img src={googlePlayBadge} alt="Get it on Google Play" className="h-9" />
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-white/15 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.22em] font-medium text-white/70">
        {optedOut ? (
          <>
            <span>You have opted out of data sharing</span>
            <button
              type="button"
              onClick={handleUndoOptOut}
              className="uppercase tracking-[0.22em] underline underline-offset-4 hover:text-white transition-colors"
            >
              Undo
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={handleOptOut}
            className="uppercase tracking-[0.22em] text-left hover:text-white transition-colors"
          >
            Do Not Sell or Share My Personal Information
          </button>
        )}
      </div>
    </footer>
  );
};

export default Footer;
